import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, Trash2, ChevronRight, MapPin } from 'lucide-react';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import t from '../translations';

const RELATIONSHIPS = ['Spouse', 'Child', 'Parent', 'Sibling', 'Friend', 'Other'];

const SIGN_SYMBOLS = ['♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓'];

function MemberAvatar({ name, size = 44 }) {
  const initials = name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
  const colors = ['#C9A84C','#4A90D9','#2D6A4F','#9B59B6','#E67E22','#C0392B'];
  const colorIdx = name ? name.charCodeAt(0) % colors.length : 0;
  return (
    <div style={{
      width: size, height: size, borderRadius: size / 2,
      background: `${colors[colorIdx]}22`, border: `2px solid ${colors[colorIdx]}55`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <span style={{ fontSize: size * 0.38, fontWeight: 700, color: colors[colorIdx] }}>{initials}</span>
    </div>
  );
}

export default function FamilyPage() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const tr = t[lang];

  const [members,    setMembers]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [deleting,   setDeleting]   = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');

  // Form state
  const [fName,       setFName]       = useState('');
  const [fRelation,   setFRelation]   = useState('');
  const [fDate,       setFDate]       = useState('');
  const [fTime,       setFTime]       = useState('');
  const [fCityQuery,  setFCityQuery]  = useState('');
  const [fCities,     setFCities]     = useState([]);
  const [fCity,       setFCity]       = useState(null);
  const [fGender,     setFGender]     = useState('');
  const [fSearching,  setFSearching]  = useState(false);

  const fetchMembers = useCallback(async () => {
    try { const d = await api.get('/api/family'); setMembers(d.members || []); }
    catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  // City search
  useEffect(() => {
    if (fCityQuery.length < 2) { setFCities([]); return; }
    const timer = setTimeout(async () => {
      setFSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(fCityQuery)}&format=json&limit=5&addressdetails=1`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        setFCities(data.filter(d => d.lat && d.lon).map(d => {
          const a = d.address || {};
          return { name: [a.city || a.town || a.village || a.county, a.state, a.country].filter(Boolean).join(', '), lat: parseFloat(d.lat), lng: parseFloat(d.lon) };
        }));
      } catch { /* silent */ }
      setFSearching(false);
    }, 450);
    return () => clearTimeout(timer);
  }, [fCityQuery]);

  const resetForm = () => {
    setFName(''); setFRelation(''); setFDate(''); setFTime('');
    setFCityQuery(''); setFCities([]); setFCity(null); setFGender('');
    setError('');
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!fCity) { setError('Please select a birth city.'); return; }
    if (!fRelation) { setError('Please select relationship.'); return; }
    setSaving(true); setError('');
    try {
      const member = await api.post('/api/family', {
        name: fName, relationship: fRelation,
        birthDate: fDate, birthTime: fTime || null,
        birthCity: fCity.name, birthLat: fCity.lat, birthLng: fCity.lng,
        gender: fGender || null,
      });
      setMembers(m => [...m, member]);
      setShowForm(false);
      resetForm();
    } catch (err) {
      setError(err.message || 'Failed to add member');
    }
    setSaving(false);
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Remove this family member?')) return;
    setDeleting(memberId);
    try {
      await api.delete(`/api/family/${memberId}`);
      setMembers(m => m.filter(x => x.memberId !== memberId));
    } catch { /* silent */ }
    setDeleting(null);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0A0F1E', paddingBottom: 48 }}>

      {/* Header */}
      <div style={{ padding: '56px 20px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/home')} style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', padding: 0 }}>
            <ChevronLeft size={22} />
          </button>
          <div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: '#F5F0E8', fontWeight: 700 }}>
              {tr.familyTitle}
            </h1>
            <p style={{ fontSize: 13, color: '#8A8580', marginTop: 2 }}>{tr.familySubtitle}</p>
          </div>
        </div>
        <button
          data-testid="add-family-btn"
          onClick={() => { setShowForm(v => !v); if (showForm) resetForm(); }}
          style={{
            background: showForm ? 'rgba(192,57,43,0.1)' : 'rgba(201,168,76,0.1)',
            border: `1px solid ${showForm ? 'rgba(192,57,43,0.3)' : 'rgba(201,168,76,0.3)'}`,
            borderRadius: 10, padding: '8px 14px', cursor: 'pointer',
            color: showForm ? '#C0392B' : '#C9A84C', fontSize: 13, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <Plus size={15} style={{ transform: showForm ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s' }} />
          {showForm ? 'Cancel' : tr.addMember}
        </button>
      </div>

      <div style={{ padding: '0 20px' }}>

        {/* ── Add Form ── */}
        {showForm && (
          <form onSubmit={handleAdd} className="card fade-in-up" noValidate style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 12, color: '#C9A84C', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              New Family Member
            </p>

            <input data-testid="fm-name" type="text" className="input-field" placeholder="Full Name" value={fName} onChange={e => setFName(e.target.value)} required />

            {/* Relationship */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {RELATIONSHIPS.map(r => (
                <button key={r} type="button"
                  onClick={() => setFRelation(r)}
                  style={{
                    padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: `1px solid ${fRelation === r ? '#C9A84C' : 'rgba(255,255,255,0.1)'}`,
                    background: fRelation === r ? 'rgba(201,168,76,0.12)' : 'transparent',
                    color: fRelation === r ? '#C9A84C' : '#8A8580', transition: 'all 0.15s',
                  }}>
                  {r}
                </button>
              ))}
            </div>

            <input data-testid="fm-birth-date" type="date" className="input-field" placeholder="Birth Date" value={fDate} onChange={e => setFDate(e.target.value)} required max={new Date().toISOString().split('T')[0]} />
            <input data-testid="fm-birth-time" type="time" className="input-field" placeholder="Birth Time (optional)" value={fTime} onChange={e => setFTime(e.target.value)} />

            {/* City */}
            {fCity ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <MapPin size={14} color="#C9A84C" />
                  <span style={{ fontSize: 13, color: '#F5F0E8' }}>{fCity.name}</span>
                </div>
                <button type="button" onClick={() => { setFCity(null); setFCityQuery(''); }} style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', fontSize: 12 }}>Change</button>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <input data-testid="fm-birth-city" type="text" className="input-field" placeholder="Birth City" value={fCityQuery} onChange={e => setFCityQuery(e.target.value)} autoComplete="off" />
                {fSearching && <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: '#8A8580', fontSize: 12 }}>↻</div>}
                {fCities.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: '#1a2335', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 12, marginTop: 4, overflow: 'hidden' }}>
                    {fCities.map((c, i) => (
                      <button key={i} type="button" onMouseDown={e => { e.preventDefault(); setFCity(c); setFCityQuery(''); setFCities([]); }}
                        style={{ width: '100%', textAlign: 'left', padding: '11px 16px', background: 'transparent', border: 'none', color: '#F5F0E8', cursor: 'pointer', fontSize: 13, borderBottom: i < fCities.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                        <MapPin size={11} style={{ display: 'inline', marginRight: 6, color: '#C9A84C' }} />{c.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Gender */}
            <div style={{ display: 'flex', gap: 8 }}>
              {[{v:'male',l:'Male'},{v:'female',l:'Female'},{v:'other',l:'Other'}].map(g => (
                <button key={g.v} type="button" onClick={() => setFGender(g.v)}
                  style={{ flex: 1, padding: '9px 4px', borderRadius: 10, border: `1px solid ${fGender === g.v ? '#C9A84C' : 'rgba(255,255,255,0.08)'}`, background: fGender === g.v ? 'rgba(201,168,76,0.1)' : 'transparent', color: fGender === g.v ? '#C9A84C' : '#8A8580', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  {g.l}
                </button>
              ))}
            </div>

            {error && <p style={{ color: '#C0392B', fontSize: 13 }}>{error}</p>}
            <button data-testid="fm-submit-btn" type="submit" className="btn-primary" disabled={saving || !fName || !fRelation || !fDate || !fCity} style={{ padding: '13px' }}>
              {saving ? 'Building chart…' : 'Add Family Member'}
            </button>
          </form>
        )}

        {/* ── Member List ── */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[0,1].map(i => (
              <div key={i} style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, padding: '16px', display: 'flex', gap: 12 }}>
                <div className="skeleton" style={{ width: 44, height: 44, borderRadius: 22 }} />
                <div style={{ flex: 1 }}>
                  <div className="skeleton" style={{ height: 14, width: '50%', marginBottom: 8 }} />
                  <div className="skeleton" style={{ height: 11, width: '30%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : members.length === 0 && !showForm ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <p style={{ fontSize: 36, marginBottom: 12 }}>👨‍👩‍👧‍👦</p>
            <p style={{ fontSize: 16, color: '#F5F0E8', fontWeight: 600, marginBottom: 8 }}>{tr.noFamilyMembers}</p>
            <p style={{ fontSize: 13, color: '#8A8580', lineHeight: 1.5 }}>{tr.noFamilyHint}</p>
            <button className="btn-primary" onClick={() => setShowForm(true)} style={{ marginTop: 24, maxWidth: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Plus size={16} /> {tr.addMember}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {members.map((m, i) => {
              const chart = m.chart || {};
              const sunSign = chart.planets?.find(p => p.name === 'Sun')?.sign?.en || '';
              const signIdx = chart.planets?.find(p => p.name === 'Sun')?.houseNum;
              const symbol = chart.planets?.find(p => p.name === 'Sun')?.sign?.symbol || '✦';
              return (
                <div key={m.memberId} data-testid={`family-card-${m.memberId}`} style={{
                  background: '#111827', border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 14, padding: '14px 16px',
                  display: 'flex', alignItems: 'center', gap: 14,
                  animation: `fadeInUp 0.3s ease ${i * 0.07}s forwards`, opacity: 0,
                }}>
                  <MemberAvatar name={m.name} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 15, fontWeight: 700, color: '#F5F0E8', fontFamily: "'Playfair Display', serif" }}>{m.name}</p>
                    <p style={{ fontSize: 11, color: '#8A8580', marginTop: 2 }}>
                      {m.relationship} · {symbol} {sunSign} · {m.birthDate}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <button
                      data-testid={`family-reading-btn-${m.memberId}`}
                      onClick={() => navigate(`/family/${m.memberId}/reading`, { state: { member: m } })}
                      style={{
                        background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)',
                        borderRadius: 8, padding: '7px 12px', cursor: 'pointer',
                        color: '#C9A84C', fontSize: 12, fontWeight: 600,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                      Reading <ChevronRight size={13} />
                    </button>
                    <button
                      data-testid={`family-delete-btn-${m.memberId}`}
                      onClick={() => handleDelete(m.memberId)}
                      disabled={deleting === m.memberId}
                      style={{
                        background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.15)',
                        borderRadius: 8, padding: '7px 10px', cursor: 'pointer', color: '#C0392B',
                      }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
